// Page side wrapper around the analyzer worker.
//
// Falls back to running the same engine on the main thread when the browser
// refuses to start a worker. In that mode the UI stays usable because the reader
// yields between chunks, but it is noticeably less smooth, which the UI says.

import { createEngine } from './engine'

let client = null

function createWorker() {
    try {
        // webpack resolves this to a separate same-origin chunk, which the
        // application CSP allows through `worker-src 'self'`.
        return new Worker(new URL('./analyzer.worker.js', import.meta.url))
    } catch (error) {
        console.warn('Combat log worker unavailable, falling back to the main thread', error)
        return null
    }
}

function createClient() {
    const listeners = new Map()
    const pending = new Map()
    let nextRequestId = 1
    let worker = null
    let fallbackHandle = null

    function emit(name, payload) {
        const callbacks = listeners.get(name)
        if (!callbacks) return
        callbacks.forEach((callback) => {
            try {
                callback(payload)
            } catch (error) {
                console.error(`Combat log listener for ${name} failed`, error)
            }
        })
    }

    function settle(message) {
        const entry = message.requestId ? pending.get(message.requestId) : null

        switch (message.type) {
            case 'progress':
                emit('progress', message)
                return
            case 'status':
                emit('status', message)
                return
            case 'warning':
                emit('warning', message)
                return
            case 'error':
                emit('error', message)
                return
            case 'done':
                emit('done', message)
                if (entry) {
                    pending.delete(message.requestId)
                    entry.resolve(message.summary)
                }
                return
            case 'failed':
                emit('failed', message)
                if (entry) {
                    pending.delete(message.requestId)
                    entry.reject(new Error(message.message || 'Log analysis failed'))
                }
                return
            case 'segmentReport':
                if (entry) {
                    pending.delete(message.requestId)
                    entry.resolve(message.report)
                }
                return
            case 'storedSummary':
                if (entry) {
                    pending.delete(message.requestId)
                    entry.resolve({ summary: message.summary, record: message.record })
                }
                return
            case 'storedReports':
                if (entry) {
                    pending.delete(message.requestId)
                    entry.resolve(message.reports)
                }
                return
            case 'cacheStats':
                if (entry) {
                    pending.delete(message.requestId)
                    entry.resolve(message.stats)
                }
                return
            case 'cacheCleared':
                emit('cacheCleared', message)
                if (entry) {
                    pending.delete(message.requestId)
                    entry.resolve(message)
                }
                return
            case 'cacheEvicted':
                emit('cacheEvicted', message)
                return
            default:
                return
        }
    }

    function ensureTransport() {
        if (worker || fallbackHandle) return
        worker = createWorker()
        if (worker) {
            worker.onmessage = (event) => settle(event.data)
            worker.onerror = (event) => {
                console.error('Combat log worker error', event.message || event)
                emit('error', { message: event.message || 'Worker failure' })
            }
            return
        }
        fallbackHandle = createEngine((message) => settle(message))
        emit('fallback', { active: true })
    }

    function send(message) {
        ensureTransport()
        if (worker) worker.postMessage(message)
        else fallbackHandle(message)
    }

    function request(message) {
        const requestId = nextRequestId
        nextRequestId += 1
        return new Promise((resolve, reject) => {
            pending.set(requestId, { resolve, reject })
            send({ ...message, requestId })
        })
    }

    return {
        get usesWorker() {
            // The transport is created lazily, so asking the question is what
            // decides it; without this the UI would report "no worker" simply
            // because nothing had been analyzed yet.
            ensureTransport()
            return Boolean(worker)
        },
        get isFallback() {
            ensureTransport()
            return !worker
        },
        on(name, callback) {
            if (!listeners.has(name)) listeners.set(name, new Set())
            listeners.get(name).add(callback)
            return () => listeners.get(name).delete(callback)
        },
        process(payload) {
            return request({ type: 'process', ...payload })
        },
        segment(fileId, segmentKey) {
            return request({ type: 'segment', fileId, segmentKey })
        },
        storedSummary(fileId) {
            return request({ type: 'storedSummary', fileId })
        },
        storedReports(fileId) {
            return request({ type: 'storedReports', fileId })
        },
        configure(options) {
            send({ type: 'configure', ...options })
        },
        cacheStats() {
            return request({ type: 'cacheStats' })
        },
        clearReportCache(keepFileId) {
            return request({ type: 'clearReportCache', keepFileId })
        },
        cancel(fileId) {
            send({ type: 'cancel', fileId })
        },
        drop(fileId) {
            send({ type: 'drop', fileId })
        },
    }
}

export function getAnalyzerClient() {
    if (!client) client = createClient()
    return client
}
